/**
 * RulesTraceDrawer — Sprint S8b (prep) · 2026-02c
 *
 * "Why did this fire / not fire?" diagnostic drawer that replays every
 * seeded repair rule against the current appraisal and shows:
 *   • the anchor snapshot (every measurement the rules engine reads)
 *   • per-rule checks (operation match · roof-type match)
 *   • matched vs would-fire qty · breakdown parts · confidence tier
 *
 * Backend: GET /api/appraisals/{id}/rules/trace (see rules_trace.py)
 *
 * Opens as a sliding panel on the right, dismissible via ✕ or backdrop
 * click. Keyboard: Esc closes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2, CheckCircle2, XCircle, Info, Search } from 'lucide-react';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';

export function RulesTraceDrawer({ api, appraisalId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get(`/appraisals/${appraisalId}/rules/trace`);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const anchors = data?.anchors_snapshot || {};
  const traces = data?.traces || [];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="rules-trace-drawer"
      onClick={onClose}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]" />

      {/* panel */}
      <div
        className="relative w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
              <Search size={14} className="text-orange-600" /> Rules Trace
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Why did this fire · what if</span>
            </h3>
            {data && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                {data.matched_count} of {data.total_rules} rule{data.total_rules === 1 ? '' : 's'} fire
                for roof <strong>{anchors.roof_type || '—'}</strong>
                {anchors.roof_type_normalised && anchors.roof_type_normalised !== anchors.roof_type
                  ? ` → ${anchors.roof_type_normalised}` : ''}
              </p>
            )}
          </div>
          <button
            data-testid="rules-trace-close-btn"
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        {/* body */}
        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Replaying rules against this appraisal…
            </div>
          )}
          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-3 text-sm">
              {err}
            </div>
          )}

          {data && (
            <>
              {/* Anchor snapshot */}
              <section data-testid="rules-trace-anchors" className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                  <Info size={11} /> Anchor snapshot
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono-num">
                  <AnchorRow k="Roof type"     v={anchors.roof_type || '—'} />
                  <AnchorRow k="Roof m²"       v={fmt(anchors.roof_m2, 'm²')} src={anchors.roof_m2_source} />
                  <AnchorRow k="Pitch"         v={anchors.pitch_deg != null ? `${anchors.pitch_deg.toFixed(1)}°` : '—'} src={anchors.pitch_source} />
                  <AnchorRow k="Slope factor"  v={anchors.slope_factor?.toFixed(3) || '—'} />
                  <AnchorRow k="Ridge"         v={fmt(anchors.ridge_length_m)} />
                  <AnchorRow k="Hip · slope"   v={fmt(anchors.hip_slope_m)} />
                  <AnchorRow k="Valley · slope" v={fmt(anchors.valley_slope_m)} />
                  <AnchorRow k="Barge · slope" v={fmt(anchors.barge_slope_m)} />
                  <AnchorRow k="Perimeter · gutter" v={fmt(anchors.perimeter_gutter_m)} src={anchors.perimeter_gutter_source} />
                  <AnchorRow k="Perimeter · full"   v={fmt(anchors.perimeter_full_m)}   src={anchors.perimeter_full_source} />
                </div>
              </section>

              {/* Per-rule traces */}
              <section className="space-y-2">
                <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                  Rule evaluation ({traces.length})
                </h4>
                {traces.map((t) => (
                  <TraceCard
                    key={t.rule_id}
                    trace={t}
                    expanded={expandedRuleId === t.rule_id}
                    onToggle={() => setExpandedRuleId(expandedRuleId === t.rule_id ? null : t.rule_id)}
                  />
                ))}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(n, unit = 'm') {
  const v = Number(n || 0);
  return `${v.toFixed(2)} ${unit}`;
}

function AnchorRow({ k, v, src }) {
  return (
    <>
      <div className="text-slate-500 truncate">{k}</div>
      <div className="text-slate-900 font-semibold truncate">
        {v}
        {src && src !== 'none' && (
          <span className="ml-1 text-[9px] font-normal text-slate-400 uppercase tracking-wider">· {src}</span>
        )}
      </div>
    </>
  );
}

function TraceCard({ trace, expanded, onToggle }) {
  const badgeTone = trace.matched
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  const StatusIcon = trace.matched ? CheckCircle2 : XCircle;
  const statusColor = trace.matched ? 'text-emerald-600' : 'text-slate-400';

  return (
    <div
      data-testid={`rules-trace-card-${trace.rule_id}`}
      className={`border rounded-md ${trace.matched ? 'border-emerald-200' : 'border-slate-200'}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`rules-trace-card-toggle-${trace.rule_id}`}
        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50"
      >
        <StatusIcon size={14} className={statusColor} strokeWidth={2.5} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-extrabold text-slate-900">{trace.rule_id}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badgeTone}`}>
              {trace.matched ? 'FIRES' : 'DOES NOT FIRE'}
            </span>
            {trace.would_fire_qty > 0 && (
              <span className="text-[10px] font-mono-num text-slate-600">
                {trace.matched ? '→' : 'would fire'} {Number(trace.would_fire_qty).toFixed(2)} {trace.would_fire_unit}
              </span>
            )}
            <ConfidenceBanner source={undefined} confidence={trace.confidence} compact />
          </div>
          <div className="text-[11px] text-slate-500 truncate mt-0.5">{trace.operation_ref}</div>
        </div>
        <span className="text-slate-400 text-xs">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2 text-xs">
          <p className="text-slate-600 leading-relaxed">{trace.description}</p>
          {trace.matched_reason && !trace.matched && (
            <p className="bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1 text-[11px]">
              <strong>Why not:</strong> {trace.matched_reason}
            </p>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Checks</div>
            <ul className="space-y-1">
              {trace.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`rules-trace-check-${trace.rule_id}-${c.check}`}>
                  {c.passed
                    ? <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    : <XCircle size={12} className="text-rose-500 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1">
                    <span className="font-semibold text-slate-800">{c.check}</span>
                    <span className="ml-1 text-slate-500">
                      · expected <code className="text-[10px] bg-slate-100 px-1 rounded">{JSON.stringify(c.expected)}</code>
                      · actual <code className="text-[10px] bg-slate-100 px-1 rounded">{JSON.stringify(c.actual)}</code>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {trace.breakdown?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Breakdown</div>
              <ul className="space-y-0.5 font-mono-num">
                {trace.breakdown.map((p, i) => (
                  <li key={i} className="text-slate-700">· {p.label}: <strong>{Number(p.value).toFixed(2)}</strong></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
